import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { ReportLayout } from 'webapp/enums/enums';

import BoardsShareScheduleFormatSwitcherComponent from 'webapp/components/boards/share/schedule/format-switcher';
import BoardsShareScheduleIncludeBgImagesComponent from 'webapp/components/boards/share/schedule/include-bg-images';
import FormPasswordWithCheckboxComponent from 'webapp/components/form/password-with-checkbox';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type Report from 'webapp/models-custom/report';

export interface Args {
	model: Report;
	isContentShown: boolean;
	onContentChange: () => void;
	onClose: () => void;
}

export default class ReportsShareDownloadTabComponent extends Component<Args> {
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;

	@tracked password = '';
	@tracked isPagelessFormat = true;
	@tracked includeBgImages = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.changeFormat(this.args.model.isPageless);
	}

	objectForIncludeBgImages = {
		target: this.args.model,
	};

	get isGenerateDisabled(): boolean {
		return this.args.model.slideCount === 0;
	}

	get generateButtonTooltipText(): string {
		let text = '';
		if (this.isGenerateDisabled) {
			text = 'Report is empty. Please add some slides first.';
		}
		return text;
	}

	@action
	changePassword(value: string): void {
		this.password = value;
	}

	@action
	createReportSnapshot(): void {
		void this.mmReportsService.downloadSnapshotWithInfolayer(
			this.args.model,
			this.password,
			this.includeBgImages,
			this.isPagelessFormat ? ReportLayout.PAGELESS : ReportLayout.SLIDES,
		);

		this.args.onClose();
	}

	@action
	changeFormat(isPageless: boolean): void {
		this.isPagelessFormat = isPageless;
		if (isPageless) {
			this.includeBgImages = false;
		}
	}

	@action
	changeIncludeBgImages(selected: boolean): void {
		this.includeBgImages = selected;
	}
	<template>
		<div class="content2 {{if @isContentShown 'content2--shown'}} tab-download">

			<p>
				<strong>Generate PDF document</strong>
				({{@model.slideCount}}
				pages)
			</p>

			<BoardsShareScheduleFormatSwitcherComponent
				@isPageless={{this.isPagelessFormat}}
				@onChange={{this.changeFormat}}
			/>

			<FormPasswordWithCheckboxComponent
				@label="Protect PDF with password"
				@password={{this.password}}
				@onChange={{this.changePassword}}
			/>

			<BoardsShareScheduleIncludeBgImagesComponent
				@model={{this.objectForIncludeBgImages}}
				@isPagelessFormat={{this.isPagelessFormat}}
				@includeBgImages={{this.includeBgImages}}
				@onChange={{this.changeIncludeBgImages}}
			/>

			<div class="controls2">
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Create PDF"
						@onTrigger={{this.createReportSnapshot}}
						@isDisabled={{this.isGenerateDisabled}}
						@tooltipText={{this.generateButtonTooltipText}}
					/>
				</div>
			</div>

		</div>
	</template>
}
